import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { fetchBundle } from "@/services/api/i18n";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import pl from "./locales/pl.json";

export const SUPPORTED_LOCALES = ["en", "pl", "fr", "es"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const resources = {
  en: { translation: en },
  pl: { translation: pl },
  fr: { translation: fr },
  es: { translation: es },
} as const;

void i18n.use(initReactI18next).init({
  resources,
  lng: "en",
  fallbackLng: "en",
  ns: ["translation"],
  defaultNS: "translation",
  interpolation: {
    escapeValue: false,
  },
});

/**
 * Load the authoritative locale bundle from the authoring store and merge it
 * over the static fallback. This is what makes a locale edited in the admin
 * workbench (or a brand-new locale) appear without a rebuild. Retries a few
 * times because the dev backend can be briefly unavailable during a reload;
 * fails silently (graceful degradation) so static bundles keep the UI usable
 * offline or in tests where the API is unreachable.
 */
export async function loadLocaleFromServer(locale: string): Promise<void> {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const bundle = await fetchBundle(locale);
      i18n.addResourceBundle(locale, "translation", bundle as never, true, true);
      return;
    } catch {
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
  }
}

/** Load the server bundle for the active locale now and on every change. */
export function attachServerBundleLoader(): void {
  const refresh = () => void refreshServerBundles();
  refresh();
  i18n.on("languageChanged", (locale) => {
    void loadLocaleFromServer(locale);
  });
  // Re-fetch whenever the tab is (re)shown so edits published in the admin
  // workbench appear without a full page reload.
  if (typeof window !== "undefined") {
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
  }
}

/** Re-fetch every locale's bundle from the authoring store (e.g. after publish). */
export async function refreshServerBundles(): Promise<void> {
  await Promise.all(SUPPORTED_LOCALES.map((locale) => loadLocaleFromServer(locale)));
}

export default i18n;
